import { Link } from '@tanstack/react-router'
import { Clock, PlayCircle, Star, Users } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { formatDuration, formatNumber, formatRupiah } from '@/lib/format'
import { levelLabel } from '@/components/ui/status'
import { cn } from '@/lib/cn'
import type { Course } from '@/lib/types'

/** Kartu kelas untuk katalog dan daftar rekomendasi. */
export function CourseCard({ course, className }: { course: Course; className?: string }) {
  const discounted = course.discount_price !== undefined && course.discount_price < course.price

  return (
    <Link
      to="/kelas/$slug"
      params={{ slug: course.slug }}
      className={cn(
        'card group flex flex-col overflow-hidden transition duration-200 hover:-translate-y-0.5 hover:shadow-[var(--shadow-lifted)]',
        className,
      )}
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-sunken">
        {course.thumbnail_url ? (
          <img
            src={course.thumbnail_url}
            alt=""
            loading="lazy"
            className="size-full object-cover transition duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-brand-soft text-brand">
            <PlayCircle className="size-10" />
          </div>
        )}

        <div className="absolute left-3 top-3 flex gap-1.5">
          <Badge tone="brand">{levelLabel[course.level] ?? course.level}</Badge>
          {course.effective_price === 0 ? <Badge tone="success">Gratis</Badge> : null}
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand">{course.category}</p>
        <h3 className="clamp-2 mt-1.5 text-base font-bold leading-snug text-ink">{course.title}</h3>
        <p className="clamp-2 mt-1 text-sm text-muted">{course.subtitle}</p>

        <p className="mt-3 text-sm text-muted">{course.expert.full_name}</p>

        <div className="mb-4 mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-subtle">
          <span className="inline-flex items-center gap-1">
            <PlayCircle className="size-3.5" />
            {course.lesson_count > 0 ? `${course.lesson_count} materi` : 'Kurikulum disiapkan'}
          </span>
          {course.duration_minutes > 0 ? (
            <span className="inline-flex items-center gap-1">
              <Clock className="size-3.5" />
              {formatDuration(course.duration_minutes)}
            </span>
          ) : null}
          <span className="inline-flex items-center gap-1">
            <Users className="size-3.5" />
            {formatNumber(course.enrollment_count)} peserta
          </span>
        </div>

        <div className="mt-auto flex items-end justify-between gap-3 border-t border-line pt-3">
          <div>
            {discounted ? (
              <p className="text-xs text-subtle line-through">{formatRupiah(course.price)}</p>
            ) : null}
            <p className="text-lg font-extrabold tracking-tight text-ink">
              {course.effective_price === 0 ? 'Gratis' : formatRupiah(course.effective_price)}
            </p>
          </div>

          {course.rating_count > 0 ? (
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-ink">
              <Star className="size-4 fill-amber-400 text-amber-400" />
              {course.rating.toFixed(1)}
              <span className="text-xs font-normal text-subtle">({course.rating_count})</span>
            </span>
          ) : (
            <span className="text-xs text-subtle">Belum ada ulasan</span>
          )}
        </div>
      </div>
    </Link>
  )
}

export function CourseCardSkeleton() {
  return (
    <div className="card overflow-hidden">
      <div className="skeleton aspect-[16/9] rounded-none" />
      <div className="space-y-3 p-4">
        <div className="skeleton h-3 w-20" />
        <div className="skeleton h-4 w-full" />
        <div className="skeleton h-4 w-2/3" />
        <div className="skeleton h-3 w-1/2" />
        <div className="skeleton h-6 w-32" />
      </div>
    </div>
  )
}
